import type { Column, DataConfigTable } from 'api/shared/models';
import type { UserConfigRequest } from 'api/user/models';

const getColumnToString = ({
  label,
  width,
  apiField,
  format,
  align,
}: Column) => {
  let fortmat = '';
  const column = {
    label,
    width,
    apiField,
    format,
    align,
  };
  Object.entries(column)
    .sort((a, b) => {
      if (a > b) return -1;
      if (a < b) return 1;
      return 0;
    })
    .forEach(([key, value]) => {
      fortmat = fortmat + `${key}=${String(value || '') || ''},`;
    });

  return fortmat;
};

const checkOriginalColumns = (
  columnsDb: Column[] = [],
  columns: Column[] = [],
) => {
  if (columnsDb.length !== columns.length) {
    return {
      requiresUpdate: true,
      columns,
    };
  }

  let requiresUpdate = false;

  for (let index = 0; index < columnsDb.length; index++) {
    const columnDb = columnsDb[index];
    const column = columns[index];
    if (getColumnToString(columnDb) !== getColumnToString(column)) {
      requiresUpdate = true;
      continue;
    }
  }

  return {
    requiresUpdate,
    columns: requiresUpdate ? columns : columnsDb,
  };
};

const checkSortColumns = (columnsDb: Column[] = [], columns: Column[] = []) => {
  const newColums: Column[] = [];
  let requiresUpdate = false;

  columnsDb.forEach((columnDb) => {
    const column = columns.find(
      (itemColumn) => itemColumn.apiField === columnDb.apiField,
    );
    if (!column) {
      requiresUpdate = true;
      return;
    }
    if (getColumnToString(columnDb) !== getColumnToString(column)) {
      newColums.push({ ...column, status: columnDb.status });
      requiresUpdate = true;
      return;
    }
    newColums.push(columnDb);
  });

  return {
    requiresUpdate,
    columns: requiresUpdate ? newColums : columnsDb,
  };
};

const checkColumns = (columnsDb: Column[] = [], columns: Column[] = []) => {
  const newColums: Column[] = [];
  let requiresUpdate = false;

  columns.forEach((column) => {
    const columnDb = columnsDb.find(
      (itemColumnDb) => itemColumnDb.apiField === column.apiField,
    );

    if (!columnDb) {
      newColums.push(column);
      requiresUpdate = true;
      return;
    }

    if (getColumnToString(columnDb) !== getColumnToString(column)) {
      newColums.push({ ...column, status: columnDb.status });
      requiresUpdate = true;
      return;
    }
    newColums.push(columnDb);
  });

  if (newColums.length !== columnsDb.length) {
    requiresUpdate = true;
  }

  return {
    requiresUpdate,
    columns: requiresUpdate ? newColums : columnsDb,
  };
};

export const processCheckTableConfig = (
  tableConfig: DataConfigTable,
  columns: Column[],
) => {
  const resultCheckOriginalColumns = checkOriginalColumns(
    tableConfig.originalColumns,
    columns,
  );

  const resultCheckColumns = checkColumns(tableConfig.columns, columns);

  const resultCheckSortColumns = checkSortColumns(
    tableConfig.sortColumns,
    columns,
  );

  if (
    resultCheckOriginalColumns.requiresUpdate ||
    resultCheckColumns.requiresUpdate ||
    resultCheckSortColumns.requiresUpdate
  ) {
    const body = {
      reportKey: tableConfig.tableName,
      columnConfiguration: {
        ...tableConfig,
        columns: resultCheckColumns.requiresUpdate
          ? resultCheckColumns.columns
          : tableConfig.columns,
        originalColumns: resultCheckOriginalColumns.requiresUpdate
          ? resultCheckOriginalColumns.columns
          : tableConfig.originalColumns,
        sortColumns: resultCheckSortColumns.requiresUpdate
          ? resultCheckSortColumns.columns
          : tableConfig.sortColumns,
      },
    };

    return {
      requiresUpdate: true,
      data: body.columnConfiguration,
    };
  }

  return {
    requiresUpdate: false,
    data: tableConfig,
  };
};

export const getUserConfigRequest = (
  tableName: string,
  configVersion: number,
  columns: Column[],
): UserConfigRequest => {
  return {
    reportKey: tableName,
    columnConfiguration: {
      version: configVersion || 0,
      tableName,
      columns,
      originalColumns: columns,
      sortColumns: [],
      dense: false,
    },
  };
};
